import {useCallback, useEffect, useRef} from "react";
import useAcknowledgeAlert from "./UseAcknowledgeAlert";
import {hasCartIdentifier, safeSetSessionStorage} from "../Helpers/Globals";

export const ALERT_RESPONSE = {
    Acknowledged: 'Acknowledged',
    Declined: 'Declined',
    SendText: 'SendText',
    SendEmail: 'SendEmail',
    AwardCoupon: 'AwardCoupon',
    OptOut: 'OptOut',
    TimedOut: 'TimedOut',
    Dismissed: 'Dismissed',
}

const respondedKey = (alertUid) => `alertResponded:${alertUid}`

const hasStoredResponse = (alertUid) => {
    try {
        return !!sessionStorage.getItem(respondedKey(alertUid))
    } catch {
        return false
    }
}

const clearStoredResponse = (alertUid) => {
    try {
        sessionStorage.removeItem(respondedKey(alertUid))
    } catch {
        // ignore session storage failures
    }
}

// Sends the alert's response (AcknowledgeAlertCommand.Response) once per alert. The first explicit
// response wins: later calls for the same alert are no-ops, so closing after a button press never
// overwrites what the button recorded. The alert uid is remembered in session storage so the
// window.close() fallback reload does not respond a second time. Closing the window without choosing
// records Dismissed through the pagehide handler.
export default function useAlertResponse({serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId, alertUid}) {
    const acknowledge = useAcknowledgeAlert()
    const respondedRef = useRef(false)

    useEffect(() => {
        respondedRef.current = false
    }, [alertUid])

    const canRespond = !!alertUid && !!hasCartIdentifier(cartUid, cartExternalId, cartSourceExternalId) && !!serviceUrls && !!sessionKey

    const hasResponded = useCallback(() => respondedRef.current || hasStoredResponse(alertUid), [alertUid])

    const respond = useCallback((response, {responseCouponCode, responseTransactionUid} = {}) => {
        if (!canRespond || hasResponded()) {
            return Promise.resolve(false)
        }
        respondedRef.current = true
        safeSetSessionStorage(respondedKey(alertUid), response)
        return acknowledge.mutateAsync({
            urls: serviceUrls,
            accessKey: sessionKey,
            cartUid,
            cartExternalId,
            cartSourceExternalId,
            alertUid,
            response,
            responseCouponCode,
            responseTransactionUid,
            keepalive: true,
        }).catch((error) => {
            // Let a later response (or the close-time Dismissed) try again.
            respondedRef.current = false
            clearStoredResponse(alertUid)
            console.log(error)
            return false
        })
    }, [canRespond, hasResponded, acknowledge, serviceUrls, sessionKey, cartUid, cartExternalId, cartSourceExternalId, alertUid])

    useEffect(() => {
        const onPageHide = () => {
            if (canRespond && !hasResponded()) {
                respond(ALERT_RESPONSE.Dismissed)
            }
        }
        window.addEventListener('pagehide', onPageHide)
        return () => window.removeEventListener('pagehide', onPageHide)
    }, [canRespond, hasResponded, respond])

    return { respond, hasResponded, canRespond }
}
